import { useMemo } from "react"

import cloneDeep from "lodash-es/cloneDeep"

import { exists } from "shared/lib/exists"
import { useAuth } from "shared/lib/useAuth"
import { Project } from "shared/types/Project"

import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import {
  useFavoritesProjectQuery,
  useFavoritesQuery
} from "entities/favorites/graphql/queries.generated"
import { sortProjectChildren } from "entities/project-children/lib/sortProjectChildren"
import { useProjectFragment } from "entities/projects/api/useProjectFragment"
import {
  useProjectChildrenQuery,
  useProjectQuery
} from "entities/projects/graphql/queries.generated"
import { useProjectPermissions } from "entities/projects/model/useProjectPermissions"
import { useProjectState } from "entities/projects/model/useProjectState"

export function useProjectData(id: string | null, detailed?: boolean) {
  const { isAuthenticated } = useAuth()
  const { isPending, isUpdatingChildren } = useProjectState(id)

  const projectFragment = useProjectFragment(id ?? "")

  const projectsQueryResponse = useProjectQuery({
    variables: { id: id as string },
    skip: !id || id === FAVORITES_PROJECT_ID || !isAuthenticated
  })

  const favoritesQueryResponse = useFavoritesProjectQuery({
    skip: !id || id !== FAVORITES_PROJECT_ID || !isAuthenticated
  })

  const {
    data,
    loading,
    error: dataError,
    ...rest
  } = id === FAVORITES_PROJECT_ID
    ? favoritesQueryResponse
    : projectsQueryResponse

  const canQueryChildren =
    detailed && exists(id) && !isPending && isAuthenticated

  const projectChildrenResponse = useProjectChildrenQuery({
    variables: { id: id as string },
    skip: !canQueryChildren || id === FAVORITES_PROJECT_ID
  })

  const favoritesChildrenResponse = useFavoritesQuery({
    skip: !canQueryChildren || id !== FAVORITES_PROJECT_ID
  })

  const {
    data: childrenData,
    loading: detailsLoading,
    error: childrenError
  } = id === FAVORITES_PROJECT_ID
    ? favoritesChildrenResponse
    : projectChildrenResponse

  const project: Project | null | undefined = useMemo(() => {
    if (childrenData?.project && data?.project) {
      return {
        ...data.project,
        ...childrenData.project,
        children: sortProjectChildren(
          cloneDeep(childrenData.project.children)
        ) as Project["children"]
      }
    }
    return data?.project || projectFragment
  }, [data, childrenData, projectFragment])

  const {
    permissions,
    role,
    isLoading: isLoadingPermissions
  } = useProjectPermissions({ projectID: project?.id ?? "" })

  const isLoading = loading || isPending

  return {
    project,
    isLoading: detailed ? detailsLoading || isLoading : isLoading,
    isUpdatingChildren,
    isPending,
    isTeamProject: exists(project?.team?.id),
    permissions,
    role,
    isLoadingPermissions: isLoading || isLoadingPermissions,
    ...rest,
    error: dataError || childrenError
  }
}
